import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Store, Eye, Edit2, Trash2, BarChart3, ExternalLink, Search, Lock, Crown } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const MyShops = () => {
  const [shops, setShops] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [shopsRes, limitsRes] = await Promise.all([
        api.get('/shops/my-shops'),
        api.get('/shops/my-limits')
      ]);
      setShops(shopsRes.data);
      setLimits(limitsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load shops');
    } finally {
      setLoading(false);
    }
  };

  const deleteShop = async (shopId) => {
    if (!confirm('Are you sure you want to delete this shop?')) return;
    
    try {
      await api.delete(`/shops/my-shops/${shopId}`);
      toast.success('Shop deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete shop');
    }
  };

  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    shop.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canCreateShop = limits?.can_create && (limits?.limits?.max_shops === -1 || shops.length < limits?.limits?.max_shops);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="my-shops-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Store className="w-8 h-8 text-primary" />
              My Shops
            </h1>
            <p className="text-muted-foreground">Create and manage your online stores</p>
          </div>
          
          {canCreateShop ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="create-shop-btn"
            >
              <Plus className="w-5 h-5" />
              Create Shop
            </button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-500 rounded-lg">
              <Lock className="w-4 h-4" />
              <span className="text-sm">
                {!limits?.can_create 
                  ? 'Shop creation not allowed' 
                  : `Limit reached (${shops.length}/${limits?.limits?.max_shops})`}
              </span>
            </div>
          )}
        </div>

        {/* Limits Info */}
        {limits && (
          <div className="glass-card p-4 mb-6 flex flex-wrap gap-6 items-center">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              <span className="text-sm">
                Shops: <strong>{shops.length}</strong> / {limits.limits?.max_shops === -1 ? '∞' : limits.limits?.max_shops}
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              Products per shop: <strong>{limits.limits?.max_products_per_shop === -1 ? '∞' : limits.limits?.max_products_per_shop}</strong>
            </div>
            <div className="text-sm text-muted-foreground">
              Sections per shop: <strong>{limits.limits?.max_sections_per_shop === -1 ? '∞' : limits.limits?.max_sections_per_shop}</strong>
            </div>
            {limits.limits?.allowed_templates?.length > 0 && (
              <div className="text-sm text-muted-foreground">
                Templates: <strong>{limits.limits.allowed_templates.join(', ')}</strong>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        {shops.length > 0 && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search shops..."
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
          </div>
        )}

        {/* Shops Grid */}
        {filteredShops.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredShops.map(shop => (
              <div
                key={shop.id}
                className="glass-card p-6 hover:border-primary/50 transition-all"
                data-testid={`shop-card-${shop.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{shop.name}</h3>
                    <p className="text-sm text-muted-foreground">/{shop.slug}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    shop.is_published 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-yellow-500/20 text-yellow-500'
                  }`}>
                    {shop.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>

                {shop.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{shop.description}</p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-white/5 rounded-lg">
                  <div className="text-center">
                    <p className="text-lg font-bold text-primary">{shop.products_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Products</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold">{shop.views_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Views</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold">{shop.orders_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Orders</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Link
                    to={`/my-shops/${shop.id}/edit`}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </Link>
                  {shop.is_published && (
                    <a
                      href={`/shop/${shop.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10"
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => deleteShop(shop.id)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card p-12 text-center">
            <Store className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Shops Yet</h3>
            <p className="text-muted-foreground mb-6">
              {canCreateShop 
                ? 'Create your first online shop and start selling!' 
                : 'Shop creation is not available for your account.'}
            </p>
            {canCreateShop && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                Create Your First Shop
              </button>
            )}
          </div>
        )}

        {/* Create Shop Modal */}
        {showCreateModal && (
          <CreateShopModal
            limits={limits}
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};

// Create Shop Modal Component
const CreateShopModal = ({ limits, onClose, onCreated }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formData, setFormData] = useState({
    type: 'ecommerce',
    template: '',
    name: '',
    slug: '',
    description: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/shops/templates');
      let availableTemplates = Object.entries(res.data.templates);
      
      // Filter by allowed templates if specified
      if (limits?.limits?.allowed_templates?.length > 0) {
        availableTemplates = availableTemplates.filter(([key]) => 
          limits.limits.allowed_templates.includes(key)
        );
      }
      
      setTemplates(availableTemplates);
      // Default to first Flipkart template if available
      const flipkartTemplate = availableTemplates.find(([key, t]) => t.is_flipkart_style);
      if (flipkartTemplate) {
        setFormData(prev => ({ ...prev, template: flipkartTemplate[0] }));
      } else if (availableTemplates.length > 0) {
        setFormData(prev => ({ ...prev, template: availableTemplates[0][0] }));
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name)
    });
  };

  const createShop = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a shop name');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/shops/my-shops/create', formData);
      toast.success('Shop created successfully!');
      navigate(`/my-shops/${res.data.id}/edit`);
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shop');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced template styling
  const templateStyles = {
    // Metaverse Templates
    metaverse_store: { gradient: 'from-cyan-500 via-purple-500 to-pink-500', icon: '✨', category: 'metaverse' },
    cyberpunk_market: { gradient: 'from-pink-600 via-purple-600 to-blue-600', icon: '⚡', category: 'metaverse' },
    hologram_shop: { gradient: 'from-emerald-400 via-cyan-500 to-blue-500', icon: '🌐', category: 'metaverse' },
    quantum_commerce: { gradient: 'from-violet-500 via-purple-500 to-pink-500', icon: '🔮', category: 'metaverse' },
    // Flipkart Style
    flipkart_marketplace: { gradient: 'from-blue-600 to-blue-800', icon: '🛒', category: 'flipkart' },
    amazon_marketplace: { gradient: 'from-amber-500 to-amber-700', icon: '👑', category: 'flipkart' },
    electronics_store: { gradient: 'from-cyan-500 to-blue-600', icon: '📱', category: 'flipkart' },
    fashion_store: { gradient: 'from-pink-500 to-rose-600', icon: '👗', category: 'flipkart' },
    grocery_store: { gradient: 'from-green-500 to-emerald-600', icon: '🥬', category: 'flipkart' },
    jewelry_store: { gradient: 'from-yellow-500 to-amber-600', icon: '💎', category: 'flipkart' },
    furniture_store: { gradient: 'from-amber-700 to-amber-900', icon: '🛋️', category: 'flipkart' },
    beauty_store: { gradient: 'from-pink-400 to-purple-500', icon: '💄', category: 'flipkart' },
    sports_store: { gradient: 'from-orange-500 to-red-600', icon: '⚽', category: 'flipkart' },
    retail_store: { gradient: 'from-indigo-500 to-purple-600', icon: '🏪', category: 'flipkart' },
    // Classic
    ecommerce_starter: { gradient: 'from-amber-500 to-orange-600', icon: '🛍️', category: 'classic' },
    portfolio_minimal: { gradient: 'from-emerald-500 to-teal-600', icon: '📂', category: 'classic' },
    business_professional: { gradient: 'from-blue-500 to-indigo-600', icon: '💼', category: 'classic' },
    service_provider: { gradient: 'from-pink-500 to-rose-600', icon: '🔧', category: 'classic' },
    landing_page: { gradient: 'from-purple-500 to-violet-600', icon: '🚀', category: 'classic' },
  };

  const categories = [
    { id: 'all', name: 'All Templates' },
    { id: 'flipkart', name: 'Flipkart-Style' },
    { id: 'metaverse', name: 'Metaverse' },
    { id: 'classic', name: 'Classic' },
  ];

  const filteredTemplates = templates.filter(([key, template]) => {
    if (selectedCategory === 'all') return true;
    const style = templateStyles[key];
    if (selectedCategory === 'flipkart') return template.is_flipkart_style || style?.category === 'flipkart';
    if (selectedCategory === 'metaverse') return style?.category === 'metaverse';
    if (selectedCategory === 'classic') return !template.is_flipkart_style && style?.category !== 'metaverse';
    return true;
  });

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-card p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Create New Shop</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-6">
          {['Template', 'Details'].map((label, idx) => (
            <div key={idx} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                idx + 1 <= step ? 'bg-primary text-primary-foreground' : 'bg-white/10'
              }`}>
                {idx + 1}
              </div>
              <span className={`text-sm ${idx + 1 <= step ? 'text-primary' : 'text-muted-foreground'}`}>{label}</span>
              {idx < 1 && <div className={`flex-1 h-1 rounded ${idx + 1 < step ? 'bg-primary' : 'bg-white/10'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Choose Template */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap mb-4">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {cat.name}
                  {cat.id !== 'all' && (
                    <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                      {templates.filter(([key, t]) => {
                        const style = templateStyles[key];
                        if (cat.id === 'flipkart') return t.is_flipkart_style || style?.category === 'flipkart';
                        if (cat.id === 'metaverse') return style?.category === 'metaverse';
                        if (cat.id === 'classic') return !t.is_flipkart_style && style?.category !== 'metaverse';
                        return false;
                      }).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[50vh] overflow-y-auto pr-2">
              {filteredTemplates.map(([key, template]) => {
                const style = templateStyles[key] || { gradient: 'from-gray-500 to-gray-600', icon: '🏪' };
                const isSelected = formData.template === key;

                return (
                  <button
                    key={key}
                    onClick={() => setFormData({ ...formData, template: key })}
                    className={`relative p-0 rounded-2xl border-2 transition-all overflow-hidden text-left ${
                      isSelected
                        ? 'border-primary ring-2 ring-primary/30'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    {/* Gradient Preview */}
                    <div className={`h-24 bg-gradient-to-br ${style.gradient} relative`}>
                      <div className="absolute inset-0 bg-black/20" />
                      <span className="absolute top-2 left-2 text-2xl">{style.icon}</span>
                      {template.is_flipkart_style && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-yellow-500 text-black text-[10px] font-bold rounded">
                          FLIPKART
                        </span>
                      )}
                      {style.category === 'metaverse' && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 bg-cyan-500 text-black text-[10px] font-bold rounded">
                          METAVERSE
                        </span>
                      )}
                      {isSelected && (
                        <div className="absolute bottom-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-3 bg-card">
                      <p className="font-semibold text-sm truncate">{template.name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{template.description}</p>
                      {template.features && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {template.features.slice(0, 2).map((f, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-white/5 rounded text-[9px] text-muted-foreground capitalize">
                              {f.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/10">
              <button onClick={onClose} className="px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10">
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!formData.template}
                className="btn-primary disabled:opacity-50"
              >
                Next: Shop Details
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Shop Details */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Selected Template Preview */}
            {formData.template && (
              <div className="p-4 bg-white/5 rounded-xl flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${templateStyles[formData.template]?.gradient || 'from-gray-500 to-gray-600'} flex items-center justify-center text-2xl`}>
                  {templateStyles[formData.template]?.icon || '🏪'}
                </div>
                <div>
                  <p className="font-semibold">{templates.find(([k]) => k === formData.template)?.[1]?.name}</p>
                  <p className="text-sm text-muted-foreground">Selected Template</p>
                </div>
                <button onClick={() => setStep(1)} className="ml-auto text-sm text-primary hover:underline">
                  Change
                </button>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Shop Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
                placeholder="My Awesome Shop"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">URL Slug</label>
              <div className="flex items-center px-4 py-3 bg-white/5 border border-white/10 rounded-xl">
                <span className="text-muted-foreground">/shop/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="flex-1 bg-transparent outline-none"
                  placeholder="my-awesome-shop"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
                rows={3}
                placeholder="Describe your shop..."
              />
            </div>
            <div className="flex justify-between gap-3 mt-6 pt-4 border-t border-white/10">
              <button onClick={() => setStep(1)} className="px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10">
                Back
              </button>
              <button
                onClick={createShop}
                disabled={loading || !formData.name.trim()}
                className="btn-primary disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Store className="w-4 h-4" />
                    Create Shop
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyShops;
